import React from 'react'
import styled from 'styled-components'
import theme from 'stories/utils/theme'
import Stack from 'components/elements/Stack'
import { LayerOneTopic, SubjectTopic } from 'interfaces/Taxonomy'
import Link from 'next/link'
import EducationHatIcon from 'assets/icons/education_hat.svg'
import useMedia from 'lib/hooks/useMedia'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import { taxonomyLevelThree } from 'lib/constants/routes'
import StudyNotesIcon from 'assets/taxonomy/taxonomy_study_notes.svg'
import { Headline3 } from 'stories/elements/Typography/Text'
import Row from 'components/elements/Row'
import _useTranslation from 'lib/hooks/_useTranslation'
import FeedKnowCard from 'stories/elements/Know/FeedKnowCard'

const Container = styled(Stack)`
    h2 {
        font-family: var(--font-inter);
        font-size: 24px;
        font-weight: 600;
        line-height: 32px;
        color: ${theme.colors.subtitleBlack};
        margin: 0;

        /* Reset */
        text-transform: none;

        @media (max-width: 750px) {
            font-size: 16px;
            font-weight: 700;
            line-height: 24px;
        }
    }
    h3 {
        font-family: var(--font-inter);
        font-size: 18px;
        font-weight: 500;
        line-height: 28px;
        color: ${theme.colors.white};
        margin: 0;

        /* Reset */
        text-transform: none;

        @media (max-width: 750px) {
            font-size: 15px;
            line-height: 20px;
        }
    }
    .taxonomy-grid-stack {
        padding: 0px 10px;
        .best-study-notes {
            color: ${theme.colors.grayScaleSecondary};
        }
        svg path {
            stroke: ${theme.colors.grayScaleSecondary};
        }
    }
`

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    svg {
        position: relative;
        bottom: 1px;
        path {
            stroke: ${theme.colors.grayScaleSecondary};
        }
    }
    h2 {
        font-family: var(--font-inter);
        font-size: 24px;
        font-weight: 600;
        line-height: 32px;
        color: ${theme.colors.white};
    }
`

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;

    gap: 50px;
    @media (max-width: 1250px) {
        grid-template-columns: 1fr 1fr;
    }
    @media (max-width: 750px) {
        grid-template-columns: 1fr;
        gap: 30px;
    }
`

const TaxonomyContainer = styled.div`
    display: grid;
    gap: 5px;
    width: 350px;
    cursor: pointer;
    svg path {
        stroke: ${theme.colors.deepSeaBlue};
    }
    @media (max-width: 750px) {
        width: 100%;
    }
`

const KnowGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 15px;

    @media (max-width: 1050px) {
        grid-template-columns: 1fr;
    }
`

const HrefAnchor = styled.div`
    position: relative;
    bottom: 150px;
    height: 0px;
    width: 0px;
`

interface Props {
    taxonomy: LayerOneTopic['children']
    parentTopic: SubjectTopic
}

const TaxonomyGrid = ({ taxonomy, parentTopic }: Props) => {
    const md = useMedia(deviceSize.md)
    const { t } = _useTranslation('subjects')

    return (
        <Container gutter={md ? 80 : 100}>
            {taxonomy?.length
                ? taxonomy.map((tax) => (
                      <Stack key={tax.uuid} gutter={40}>
                          <TitleRow>
                              <HrefAnchor id={tax.slug} />
                              <EducationHatIcon />
                              <h2>{tax.name}</h2>
                          </TitleRow>
                          {tax.knows?.length ? (
                              <Stack className="taxonomy-grid-stack">
                                  <Row>
                                      <StudyNotesIcon />
                                      <Headline3
                                          fontSize={14}
                                          fontWeight={400}
                                          color={theme.colors.grayScaleSecondary}
                                          className="best-study-notes"
                                      >
                                          {t('subjects/bestStudyNotesForSnippet', {
                                              knowsCount: tax.knows.length,
                                              topic: tax.name,
                                          })}
                                      </Headline3>
                                  </Row>
                                  <KnowGrid>
                                      {tax.knows.map((know) => (
                                          <FeedKnowCard
                                              knowUuid={know.uuid}
                                              know={know}
                                              key={know.uuid}
                                              referrerScreen="taxonomy"
                                              isDarkTheme
                                          />
                                      ))}
                                  </KnowGrid>
                              </Stack>
                          ) : null}
                          {tax.children?.length ? (
                              <Grid>
                                  {tax.children.map((subtopic) => (
                                      <Link href={taxonomyLevelThree(parentTopic.slug, subtopic.slug)} key={subtopic.uuid}>
                                          <TaxonomyContainer>
                                              <h3>{subtopic.name}</h3>
                                          </TaxonomyContainer>
                                      </Link>
                                  ))}
                              </Grid>
                          ) : null}
                      </Stack>
                  ))
                : null}
        </Container>
    )
}

export default TaxonomyGrid
